import { defineMessages } from 'react-intl';

/**
 * Names of the Business & Finance section as agreed with the product owner on
 * 18–21.09.2026 (task of 25.09.2026). New ids rather than new texts on the old
 * ids: the translation manager keeps an existing id's stored translation, so a
 * renamed English text would never reach the Serbian catalog.
 */
export default defineMessages({
  performance: {
    id: 'businessFinance.performance',
    defaultMessage: 'Performance',
  },
  performanceSubtitle: {
    id: 'businessFinance.performanceSubtitle',
    defaultMessage:
      'Measured by the app, no manual entry · work done and revenue earned',
  },
  tabOverview: {
    id: 'businessFinance.tabOverview',
    defaultMessage: 'Overview',
  },
  tabClientsGroups: {
    id: 'businessFinance.tabClientsGroups',
    defaultMessage: 'Clients & groups',
  },
  viewClientsList: {
    id: 'businessFinance.viewClientsList',
    defaultMessage: 'View clients list',
  },
  tabMemberships: {
    id: 'businessFinance.tabMemberships',
    defaultMessage: 'Memberships',
  },
  tabRevenue: {
    id: 'businessFinance.tabRevenue',
    defaultMessage: 'Revenue',
  },
  tabTrainings: {
    id: 'businessFinance.tabTrainings',
    defaultMessage: 'Trainings',
  },
  tabSchedule: {
    id: 'businessFinance.tabSchedule',
    defaultMessage: 'Schedule',
  },
  tabRetention: {
    id: 'businessFinance.tabRetention',
    defaultMessage: 'Retention & Churn',
  },
  // PO 08.10.2026 — "Session statistics" is renamed and sits next to
  // Trainings; a new id, so the Serbian catalog gets the new name too
  tabBookingSessions: {
    id: 'businessFinance.tabBookingSessions',
    defaultMessage: 'Booking Sessions',
  },
  membershipsAllHeading: {
    id: 'businessFinance.membershipsAllHeading',
    defaultMessage: 'All memberships',
  },
  cardSubscriptionsHeading: {
    id: 'businessFinance.cardSubscriptionsHeading',
    defaultMessage: 'Card subscriptions',
  },
  profitsAndExpenses: {
    id: 'businessFinance.profitsAndExpenses',
    defaultMessage: 'Profits & Expenses',
  },
  profitsAndExpensesSubtitle: {
    id: 'businessFinance.profitsAndExpensesSubtitle',
    defaultMessage:
      'The owner enters official and unofficial items · added to the revenue from Performance',
  },
  tabIncome: {
    id: 'businessFinance.tabIncome',
    defaultMessage: 'Income',
  },
  tabExpenses: {
    id: 'businessFinance.tabExpenses',
    defaultMessage: 'Expenses',
  },
  incomeAutoCaption: {
    id: 'businessFinance.incomeAutoCaption',
    defaultMessage: 'Automatic — taken over from Performance',
  },
  expensesAutoCaption: {
    id: 'businessFinance.expensesAutoCaption',
    defaultMessage: 'Automatic — what the organization pays {brand}',
  },
  marketplacePayouts: {
    id: 'businessFinance.marketplacePayouts',
    defaultMessage: 'Marketplace Payouts',
  },
  marketplacePayoutsSubtitle: {
    id: 'businessFinance.marketplacePayoutsSubtitle',
    defaultMessage:
      'What {brand} pays you from marketplace sales, after margin, commission and fees',
  },
  tabSalesAndPayouts: {
    id: 'businessFinance.tabSalesAndPayouts',
    defaultMessage: 'Sales & payouts',
  },
  platformAnalytics: {
    id: 'businessFinance.platformAnalytics',
    defaultMessage: '{brand} Platform Analytics',
  },
  platformAnalyticsSubtitle: {
    id: 'businessFinance.platformAnalyticsSubtitle',
    defaultMessage: 'Insights across the whole {brand} platform',
  },
  platformAnalyticsPrice: {
    id: 'businessFinance.platformAnalyticsPrice',
    defaultMessage: '€9.99',
  },
  // Task 4.5 — disabled at launch, opened by config (PLATFORM_ANALYTICS_ENABLED).
  platformAnalyticsComingSoon: {
    id: 'businessFinance.platformAnalyticsComingSoon',
    defaultMessage: 'Coming soon — we are collecting enough data',
  },
  brandInvoices: {
    id: 'businessFinance.brandInvoices',
    defaultMessage: '{brand} Invoices',
  },
  sessionRevenueExplainV2: {
    id: 'businessFinance.sessionRevenueExplainV2',
    defaultMessage:
      'Seats paid from a membership are shown as attributed value, not as collected money. Lost revenue is theoretical and never part of any total.',
  },
  sessionRevenueAttributed: {
    id: 'businessFinance.sessionRevenueAttributed',
    defaultMessage: 'Attributed from memberships',
  },
  sessionRevenueLost: {
    id: 'businessFinance.sessionRevenueLost',
    defaultMessage: 'Lost revenue — theoretical',
  },
  sessionRevenueLostCaption: {
    id: 'businessFinance.sessionRevenueLostCaption',
    defaultMessage: 'Empty seats × price · not included in totals',
  },
  sessionRevenueColAttributed: {
    id: 'businessFinance.sessionRevenueColAttributed',
    defaultMessage: 'Attributed',
  },
  sessionRevenueColLost: {
    id: 'businessFinance.sessionRevenueColLost',
    defaultMessage: 'Theoretical (lost)',
  },
  sessionRevenueTypeFilterLabel: {
    id: 'businessFinance.sessionRevenueTypeFilterLabel',
    defaultMessage: 'Session type',
  },
  sessionRevenueTypeAll: {
    id: 'businessFinance.sessionRevenueTypeAll',
    defaultMessage: 'All types',
  },
  sessionRevenueHourChartTitle: {
    id: 'businessFinance.sessionRevenueHourChartTitle',
    defaultMessage: 'Gross by hour',
  },
  sessionRevenueTitle: {
    id: 'businessFinance.sessionRevenueTitle',
    defaultMessage: 'Gross revenue from sessions',
  },
  sessionRevenueTotal: {
    id: 'businessFinance.sessionRevenueTotal',
    defaultMessage: 'Gross session revenue',
  },
  sessionRevenueCollected: {
    id: 'businessFinance.sessionRevenueCollected',
    defaultMessage: 'Collected (after refunds)',
  },
  sessionRevenuePerSession: {
    id: 'businessFinance.sessionRevenuePerSession',
    defaultMessage: 'Gross per session',
  },
  sessionRevenuePaidSeats: {
    id: 'businessFinance.sessionRevenuePaidSeats',
    defaultMessage: 'Paid seats',
  },
  sessionRevenueByType: {
    id: 'businessFinance.sessionRevenueByType',
    defaultMessage: 'Gross by session type',
  },
  sessionRevenueBySession: {
    id: 'businessFinance.sessionRevenueBySession',
    defaultMessage: 'Sessions',
  },
  sessionRevenueColSession: {
    id: 'businessFinance.sessionRevenueColSession',
    defaultMessage: 'Session',
  },
  sessionRevenueColSeats: {
    id: 'businessFinance.sessionRevenueColSeats',
    defaultMessage: 'Paid seats',
  },
  sessionRevenueColGross: {
    id: 'businessFinance.sessionRevenueColGross',
    defaultMessage: 'Gross',
  },
  sessionRevenueColRefund: {
    id: 'businessFinance.sessionRevenueColRefund',
    defaultMessage: 'Refunded',
  },
  sessionRevenueColCollected: {
    id: 'businessFinance.sessionRevenueColCollected',
    defaultMessage: 'Collected',
  },
  sessionRevenueUntagged: {
    id: 'businessFinance.sessionRevenueUntagged',
    defaultMessage: 'Without type',
  },
  sessionRevenueEmpty: {
    id: 'businessFinance.sessionRevenueEmpty',
    defaultMessage: 'No paid seats in this period.',
  },
  summaryAutomatic: {
    id: 'businessFinance.summaryAutomatic',
    defaultMessage: 'Income — automatic',
  },
  summaryAutomaticCaption: {
    id: 'businessFinance.summaryAutomaticCaption',
    defaultMessage: 'collected, taken over from Performance',
  },
  summaryManual: {
    id: 'businessFinance.summaryManual',
    defaultMessage: 'Income — entered',
  },
  summaryManualCaption: {
    id: 'businessFinance.summaryManualCaption',
    defaultMessage: 'seminars, promotions, other',
  },
  summaryExpenses: {
    id: 'businessFinance.summaryExpenses',
    defaultMessage: 'Expenses',
  },
  summaryExpensesCaption: {
    id: 'businessFinance.summaryExpensesCaption',
    defaultMessage: 'entered + fees + {brand}',
  },
  summaryResult: {
    id: 'businessFinance.summaryResult',
    defaultMessage: 'Result',
  },
  summaryResultCaption: {
    id: 'businessFinance.summaryResultCaption',
    defaultMessage: 'income − expenses, for the selected months',
  },
  payoutNetTitle: {
    id: 'businessFinance.payoutNetTitle',
    defaultMessage: 'Net for payout · selected period',
  },
  payoutGrossCaption: {
    id: 'businessFinance.payoutGrossCaption',
    defaultMessage: 'of gross sales',
  },
  payoutNet: {
    id: 'businessFinance.payoutNet',
    defaultMessage: 'Net for payout',
  },
  payoutNetExplain: {
    id: 'businessFinance.payoutNetExplainLedger',
    defaultMessage:
      'What you earned from marketplace sales in the selected period, after commission, fees and refunds. The monthly statements are the record of what was actually paid out.',
  },
  payoutNetNegativeCaption: {
    id: 'businessFinance.payoutNetNegativeCaption',
    defaultMessage:
      'Negative: refunds in this period of sales made in an earlier period.',
  },
  payoutCommission: {
    id: 'businessFinance.payoutCommission',
    defaultMessage: '{brand} commission',
  },
  // Task 4.2 (PO 27.09) — 5 % all-in: a new id, not a defaultMessage edit on
  // the old one, per the translations rule (an existing id keeps its stored
  // translation forever; a changed defaultMessage never reaches either JSON).
  payoutCommissionExplain: {
    id: 'businessFinance.payoutCommissionExplainAllIn',
    defaultMessage:
      'On every marketplace sale — 5 % in total, card processing included. Covers content hosting, order handling and buyer support.',
  },
  payoutCommissionRefundExplain: {
    id: 'businessFinance.payoutCommissionRefundExplain',
    defaultMessage: 'Commission returned on refunded sales.',
  },
  payoutFee: {
    id: 'businessFinance.payoutFee',
    defaultMessage: 'Payment processing fee',
  },
  payoutFeeExplain: {
    id: 'businessFinance.payoutFeeExplain',
    defaultMessage: 'Charged by the card processor on every payment.',
  },
  payoutRefunds: {
    id: 'businessFinance.payoutRefunds',
    defaultMessage: 'Refunds',
  },
  payoutRefundsExplain: {
    id: 'businessFinance.payoutRefundsExplain',
    defaultMessage:
      'Purchases returned to buyers; they reduce the payout in the month they are refunded.',
  },
  payoutUnreconciled: {
    id: 'businessFinance.payoutUnreconciledManual',
    defaultMessage:
      'Refunds of {amount} were not reversed automatically (after payout, or partial) and need a manual correction — they are not deducted above.',
  },
  payoutUnreconciledCorrection: {
    id: 'businessFinance.payoutUnreconciledCorrection',
    defaultMessage:
      'A correction of {amount} reversed an earlier unresolved refund in this period; it is not included in the payout amount above.',
  },
  payoutUnreconciledDispute: {
    id: 'businessFinance.payoutUnreconciledDispute',
    defaultMessage: 'Disputes of {amount} are waiting for a manual correction.',
  },
});
